/**
 * Library data: the user's documents (paged), documents shared with them and
 * their outgoing shares (de-slop CHAT-10 / CHAT-11).
 *
 * - A failed document load is an error state with retry, never an empty
 *   library. A failed refresh keeps the rows already on screen.
 * - A failed share load only marks the shared section; documents still show.
 * - Documents load 100 at a time (the API cap). `loadMore` fetches the next
 *   page; `ensureAllLoaded` fetches the rest (up to LIBRARY_MAX_DOCUMENTS) so
 *   search and type filters cover every document.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { documentSharingAPI, userDocumentsAPI, type UserDocumentItem } from '../../services/api';
import type { SharedByMeDocument, SharedDocument } from '../../types';
import { LIBRARY_PAGE_SIZE, hasMoreDocuments, mergeDocumentPages } from './libraryDocuments';

export function useLibraryDocuments(isB2BUser: boolean) {
  const [items, setItems] = useState<UserDocumentItem[]>([]);
  const [sharedItems, setSharedItems] = useState<SharedDocument[]>([]);
  const [sharedByMeItems, setSharedByMeItems] = useState<SharedByMeDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loadMoreError, setLoadMoreError] = useState<unknown>(null);
  const [sharedLoadError, setSharedLoadError] = useState<unknown>(null);

  // Latest values for the paging callbacks, and a generation counter so a
  // page that arrives after a refresh started is dropped.
  const itemsRef = useRef(items);
  const totalRef = useRef(total);
  const generationRef = useRef(0);
  const pagingRef = useRef<Promise<void> | null>(null);
  itemsRef.current = items;
  totalRef.current = total;

  const load = useCallback(async (showRefresh = false) => {
    generationRef.current += 1;
    const generation = generationRef.current;
    pagingRef.current = null;
    setLoadingMore(false);
    setLoadMoreError(null);
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    const docsPromise = userDocumentsAPI.list(0, LIBRARY_PAGE_SIZE);
    const sharedPromise = isB2BUser
      ? Promise.all([documentSharingAPI.getSharedWithMe(), documentSharingAPI.getSharedByMe()])
      : Promise.resolve([[], []] as [SharedDocument[], SharedByMeDocument[]]);

    const [docs, shared] = await Promise.allSettled([docsPromise, sharedPromise]);
    if (generation !== generationRef.current) return;

    if (docs.status === 'fulfilled') {
      setItems(docs.value.documents || []);
      setTotal(docs.value.total || 0);
      setLoadError(null);
    } else {
      console.warn('Failed to load user documents');
      setLoadError(docs.reason ?? new Error('load_failed'));
    }

    if (shared.status === 'fulfilled') {
      setSharedItems(shared.value[0] || []);
      setSharedByMeItems(shared.value[1] || []);
      setSharedLoadError(null);
    } else {
      console.warn('Failed to load shared documents');
      setSharedLoadError(shared.reason ?? new Error('load_failed'));
    }

    setLoading(false);
    setRefreshing(false);
  }, [isB2BUser]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchNextPage = useCallback(async (generation: number) => {
    const loaded = itemsRef.current;
    const response = await userDocumentsAPI.list(loaded.length, LIBRARY_PAGE_SIZE);
    if (generation !== generationRef.current) return false;
    const merged = mergeDocumentPages(loaded, response.documents || []);
    itemsRef.current = merged;
    totalRef.current = response.total || 0;
    setItems(merged);
    setTotal(response.total || 0);
    // Stop if the server returned nothing new (list shrank meanwhile).
    return merged.length > loaded.length && hasMoreDocuments(merged.length, response.total || 0);
  }, []);

  const runPaging = useCallback((untilDone: boolean) => {
    if (pagingRef.current) return pagingRef.current;
    const generation = generationRef.current;
    const run = (async () => {
      setLoadingMore(true);
      setLoadMoreError(null);
      try {
        let more = hasMoreDocuments(itemsRef.current.length, totalRef.current);
        while (more) {
          more = await fetchNextPage(generation);
          if (!untilDone) break;
        }
      } catch (error) {
        if (generation === generationRef.current) setLoadMoreError(error ?? new Error('load_more_failed'));
      } finally {
        if (generation === generationRef.current) setLoadingMore(false);
        if (pagingRef.current === run) pagingRef.current = null;
      }
    })();
    pagingRef.current = run;
    return run;
  }, [fetchNextPage]);

  const loadMore = useCallback(() => runPaging(false), [runPaging]);
  const ensureAllLoaded = useCallback(() => runPaging(true), [runPaging]);

  return {
    items,
    setItems,
    sharedItems,
    sharedByMeItems,
    setSharedByMeItems,
    total,
    setTotal,
    loading,
    refreshing,
    loadingMore,
    loadError,
    loadMoreError,
    sharedLoadError,
    hasMore: hasMoreDocuments(items.length, total),
    load,
    loadMore,
    ensureAllLoaded,
  };
}
